import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sparkles, Shield, Zap, Globe } from 'lucide-react';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';

export function WelcomeDialog() {
  const { preferences, updatePreferences } = useUserPreferences();
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [hasCheckedInitialState, setHasCheckedInitialState] = useState(false);

  // Only show dialog on initial mount if preference is true
  useEffect(() => {
    if (!hasCheckedInitialState && preferences.showWelcomeDialog) {
      setOpen(true);
      setHasCheckedInitialState(true);
    } else if (!hasCheckedInitialState) {
      setHasCheckedInitialState(true);
    }
  }, [preferences.showWelcomeDialog, hasCheckedInitialState]);

  const handleClose = () => {
    if (dontShowAgain) {
      updatePreferences({ showWelcomeDialog: false });
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Welcome to Access Project Management
          </DialogTitle>
          <DialogDescription className="text-base pt-4">
            Modern project management and accounting for UK professional services organisations
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Key Features */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="accent-info h-10 w-10 rounded-lg bg-[var(--accent-bg-outline)] flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-[var(--accent-text-on-outline)]" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Demo Mode Active</h4>
                <p className="text-sm text-muted-foreground">
                  You're viewing realistic sample data. Explore freely - nothing you do will affect real projects.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="accent-success h-10 w-10 rounded-lg bg-[var(--accent-bg-outline)] flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-[var(--accent-text-on-outline)]" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Safe to Explore</h4>
                <p className="text-sm text-muted-foreground">
                  Try all features without risk. When ready, enable Live Mode in Feature Labs to use your real data.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="accent-purple h-10 w-10 rounded-lg bg-[var(--accent-bg-outline)] flex items-center justify-center flex-shrink-0">
                <Zap className="h-5 w-5 text-[var(--accent-text-on-outline)]" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Real-Time Insights</h4>
                <p className="text-sm text-muted-foreground">
                  Track utilisation, WIP, profitability, and project health in real-time with proactive alerts.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="accent-warning h-10 w-10 rounded-lg bg-[var(--accent-bg-outline)] flex items-center justify-center flex-shrink-0">
                <Globe className="h-5 w-5 text-[var(--accent-text-on-outline)]" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Built for UK Firms</h4>
                <p className="text-sm text-muted-foreground">
                  Native UK formats (GBP, DD/MM/YYYY), VAT handling, and deep Access ecosystem integration.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="accent-info bg-[var(--accent-bg-outline)] rounded-lg p-4 border border-[var(--accent-border)]">
            <h4 className="font-semibold mb-2 text-[var(--accent-text-on-outline)]">Quick Tips</h4>
            <ul className="space-y-1 text-sm text-[var(--accent-text-on-outline)]">
              <li>• Start with the <strong>Dashboard</strong> for an overview</li>
              <li>• Check <strong>Utilisation</strong> to see team capacity</li>
              <li>• Visit <strong>Feature Labs</strong> to enable new features</li>
              <li>• Use the <strong>Sensitive Data Toggle</strong> for demos</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 mr-auto">
            <Checkbox
              id="dont-show"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked as boolean)}
            />
            <label
              htmlFor="dont-show"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Don't show this again
            </label>
          </div>
          <Button onClick={handleClose} className="w-full sm:w-auto">
            Get Started
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
